import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ApplyAccountMappingsCard } from '../../components/admin/ApplyAccountMappingsCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { HistoricalImportTab } from '../../components/HistoricalImportTab';
import { ClientBankReconciliationTab } from '../../components/ClientBankReconciliationTab';
import {
  Building2,
  RefreshCw,
  Wallet,
  Users,
  Settings,
  CheckCircle,
  AlertCircle,
  Banknote,
  Link as LinkIcon,
  Unlink,
  Search,
  Filter,
  Upload,
  ArrowDownToLine,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Parse a fetch Response as JSON, surfacing a clear error when the proxy
// returns an HTML error page (gateway timeout on long-running imports).
const parseJsonSafe = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      `Server returned ${res.status} instead of JSON — the import likely timed out at the proxy but may still be running in the background. ` +
      `Wait a minute, then check Manage Batches / Import & Approve before re-sending (re-sending can create duplicate staging rows).`
    );
  }
};

const PURPOSE_OPTIONS = [
  { value: 'main_disbursement', label: 'Main Disbursement', description: 'Primary account for client disbursements', color: 'amber' },
  { value: 'investor', label: 'Investor Account', description: 'Linked to specific investor', color: 'blue' },
  { value: 'client_collections', label: 'Client Collections', description: 'For receiving client repayments', color: 'green' },
  { value: 'reserves', label: 'Reserves', description: 'Reserve/buffer accounts', color: 'purple' },
  { value: 'operating', label: 'Operating', description: 'General operating expenses', color: 'slate' },
];

const AdminInvestecPage = () => {
  const { token } = useAuth();
  // Stable reference: an inline object literal here would give child tabs a
  // new `headers` prop on every render, which (combined with a useCallback/
  // useEffect keyed on it) previously drove a runaway polling loop - see
  // memory/HANDOFF_FIX_INSTRUCTIONS.md 2026-09-10.
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [activeTab, setActiveTab] = useState('overview');
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPurpose, setFilterPurpose] = useState('all');
  
  // Staging sync dialog
  const [syncDialog, setSyncDialog] = useState({ open: false, account: null });
  // Bulk-sync dialog (send N accounts to staging in one call)
  const [bulkSyncOpen, setBulkSyncOpen] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkForm, setBulkForm] = useState({ from_date: '', to_date: '' });
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  // Manage batches (view + purge recent bulk imports)
  const [batchesDialogOpen, setBatchesDialogOpen] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchesDays, setBatchesDays] = useState(7);
  const [selectedBatches, setSelectedBatches] = useState(new Set());
  const [alsoDeleteCommitted, setAlsoDeleteCommitted] = useState(false);
  const [purging, setPurging] = useState(false);
  // Orphaned allocations dialog
  const [orphansDialogOpen, setOrphansDialogOpen] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [orphansDeleting, setOrphansDeleting] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedPreviewIds, setSelectedPreviewIds] = useState(new Set());
  const [overrideSelection, setOverrideSelection] = useState(new Set());
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [syncForm, setSyncForm] = useState({ from_date: new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0], to_date: new Date().toISOString().split('T')[0] });
  const [syncResult, setSyncResult] = useState(null);
  const [syncing_import, setSyncingImport] = useState(false);

  // Mapping dialog
  const [mappingDialog, setMappingDialog] = useState({ open: false, account: null });
  const [mappingForm, setMappingForm] = useState({
    purpose: '',
    display_name: '',
    investor_id: '',
    is_primary: false
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchStatus(), fetchAccounts(), fetchMappings(), fetchInvestors()]);
    setLoading(false);
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStatus(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch status:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/accounts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
    }
  };

  const fetchMappings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/mappings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setMappings(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
    }
  };

  const fetchInvestors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/investors-for-mapping`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInvestors(data.investors || []);
      }
    } catch (error) {
      console.error('Failed to fetch investors:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced ${result.accounts_synced} accounts`, {
          description: `Total balance: ${formatCurrency(result.total_balance)}`
        });
        fetchAll();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Sync error');
    } finally {
      setSyncing(false);
    }
  };

  const openMappingDialog = (account) => {
    const existingMapping = mappings?.mappings?.find(m => m.investec_account_id === account.account_id);
    setMappingForm({
      purpose: existingMapping?.purpose || '',
      display_name: existingMapping?.display_name || '',
      investor_id: existingMapping?.investor_id || '',
      is_primary: existingMapping?.is_primary || false
    });
    setMappingDialog({ open: true, account });
  };

  const saveMapping = async () => {
    const account = mappingDialog.account;
    if (!account || !mappingForm.purpose) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/investec/accounts/${account.account_id}/mapping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(mappingForm)
      });

      if (response.ok) {
        toast.success('Account mapping saved');
        setMappingDialog({ open: false, account: null });
        fetchMappings();
      } else {
        const error = await response.json();
        toast.error('Failed to save mapping', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error saving mapping');
    }
  };

  const removeMapping = async (accountId) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/investec/accounts/${accountId}/mapping`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Mapping removed');
        fetchMappings();
      }
    } catch (error) {
      toast.error('Failed to remove mapping');
    }
  };

  const getMappingForAccount = (accountId) => {
    return mappings?.mappings?.find(m => m.investec_account_id === accountId);
  };

  const filteredAccounts = accounts.filter(acc => {
    const mapping = getMappingForAccount(acc.account_id);
    const matchesSearch = 
      acc.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.account_number?.includes(searchTerm);
    const matchesPurpose = 
      filterPurpose === 'all' || 
      (filterPurpose === 'unmapped' && !mapping) ||
      mapping?.purpose === filterPurpose;
    return matchesSearch && matchesPurpose;
  });

  const getPurposeColor = (purpose) => {
    const opt = PURPOSE_OPTIONS.find(p => p.value === purpose);
    return opt?.color || 'slate';
  };

  const handleSyncToStaging = async (selectedIds = null, overrideUuids = null) => {
    if (!syncDialog.account) return;
    setSyncingImport(true);
    setSyncResult(null);
    try {
      const params = new URLSearchParams({ from_date: syncForm.from_date });
      if (syncForm.to_date) params.append('to_date', syncForm.to_date);
      if (selectedIds && selectedIds.length > 0) {
        params.append('selected_ids', selectedIds.join(','));
      }
      if (overrideUuids && overrideUuids.length > 0) {
        params.append('override_uuids', overrideUuids.join(','));
      }
      const res = await fetch(
        `${API_URL}/api/admin/investec/accounts/${syncDialog.account.account_id}/import-to-staging?${params}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.detail || 'Sync failed');
      setSyncResult(data);
      toast.success(`Staged ${data.staged_count} transactions for review`);
      if (data.override_applied > 0) toast.success(`Forced ${data.override_applied} previously-blocked duplicates through`);
      if (data.skipped_duplicates > 0) toast.info(`${data.skipped_duplicates} duplicates skipped (see list below to override)`);
      // Clear preview rows after a successful selective send
      if (selectedIds) {
        setPreviewRows([]);
        setSelectedPreviewIds(new Set());
      }
    } catch (e) {
      toast.error(e.message || 'Sync failed');
    } finally {
      setSyncingImport(false);
    }
  };

  const handleBulkSyncToStaging = async () => {
    if (bulkSelected.size === 0 || !bulkForm.from_date) {
      toast.error('Pick at least one account and a from-date');
      return;
    }
    setBulkRunning(true);
    setBulkResults(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/accounts/bulk-import-to-staging`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_ids: Array.from(bulkSelected),
          from_date: bulkForm.from_date,
          to_date: bulkForm.to_date || null,
        }),
      });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.detail || 'Bulk sync failed');
      setBulkResults(data);
      toast.success(`Sent to staging: ${data.total_created} txns across ${data.succeeded}/${data.requested} accounts`);
    } catch (e) {
      toast.error(e.message || 'Bulk sync failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleLoadBatches = async () => {
    setBatchesLoading(true);
    setSelectedBatches(new Set());
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/staging/batches?days=${batchesDays}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load batches');
      setBatches(data.batches || []);
      toast.info(`${data.count} batch(es) in last ${data.days} days`);
    } catch (e) {
      toast.error(e.message || 'Failed to load batches');
    } finally {
      setBatchesLoading(false);
    }
  };

  const handlePurgeBatches = async () => {
    if (selectedBatches.size === 0) {
      toast.error('Select at least one batch to purge');
      return;
    }
    const confirmMsg = `Delete ${selectedBatches.size} batch(es)`      + (alsoDeleteCommitted ? ' AND their committed rows in Reconciliation' : ' (staging only)')
      + '?\n\nThis is irreversible.';
    if (!window.confirm(confirmMsg)) return;
    setPurging(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/staging/purge-batches`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          batch_ids: Array.from(selectedBatches),
          also_delete_committed: alsoDeleteCommitted,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Purge failed');
      toast.success(data.message);
      await handleLoadBatches();
    } catch (e) {
      toast.error(e.message || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const handleLoadOrphans = async () => {
    setOrphansLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/orphaned-allocations?limit=5000`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load orphans');
      setOrphans(data.orphans || []);
      toast.info(data.message || `${data.orphan_count} orphan(s) found`);
    } catch (e) {
      toast.error(e.message || 'Failed to load orphans');
    } finally {
      setOrphansLoading(false);
    }
  };

  const handleDeleteOrphans = async () => {
    if (!window.confirm(`Delete ${orphans.length} orphaned allocation(s)?\n\nThis will:\n • Remove allocations that point at deleted bank rows\n • Reset any recorded txn (with zero remaining allocations) back to reconciliation_status="pending"`)) return;
    setOrphansDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/orphaned-allocations/delete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_text: 'DELETE-ORPHAN-ALLOCATIONS' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      toast.success(data.message || `Deleted ${data.deleted} orphan(s)`);
      setOrphans([]);
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setOrphansDeleting(false);
    }
  };

  const handlePurgeAll = async () => {
    const typed = window.prompt(
      'This will DELETE ALL Investec staging rows across every investor account. ' +
      'Type PURGE-ALL-STAGING to confirm.'
    );
    if (typed !== 'PURGE-ALL-STAGING') {
      if (typed !== null) toast.error('Confirmation text did not match. Nothing deleted.');
      return;
    }
    setPurging(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/investec/staging/purge-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_text: 'PURGE-ALL-STAGING' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Purge-all failed');
      toast.success(data.message || `Purged ${data.deleted} row(s)`);
      await handleLoadBatches();
    } catch (e) {
      toast.error(e.message || 'Purge-all failed');
    } finally {
      setPurging(false);
    }
  };


  const handleLoadPreview = async () => {
    if (!syncDialog.account) return;
    setLoadingPreview(true);
    setPreviewRows([]);
    setSelectedPreviewIds(new Set());
    try {
      const params = new URLSearchParams({ from_date: syncForm.from_date });
      if (syncForm.to_date) params.append('to_date', syncForm.to_date);
      const res = await fetch(
        `${API_URL}/api/admin/investec/accounts/${syncDialog.account.account_id}/preview-transactions?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.detail || 'Preview failed');
      setPreviewRows(data.rows || []);
      // Pre-select all 'new' rows by default
      const newIds = new Set((data.rows || []).filter(r => r.dedup_status === 'new' && r.investec_uuid).map(r => r.investec_uuid));
      setSelectedPreviewIds(newIds);
      toast.info(`Loaded ${data.total} transactions (${data.new_count} new, ${data.duplicate_count} already imported)`);
    } catch (e) {
      toast.error(e.message || 'Preview failed');
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-investec-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <Building2 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Investec Account Management</h1>
            <p className="text-slate-600">Manage bank accounts and investor mappings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status?.connected ? (
            <Badge className="bg-green-100 text-green-700" data-testid="investec-status-badge">
              <CheckCircle className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : (
            <div className="flex flex-col items-end gap-0.5 max-w-md">
              <Badge
                className={`${status?.status === 'not_configured' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`}
                title={status?.message || 'Disconnected'}
                data-testid="investec-status-badge"
              >
                <AlertCircle className="h-3 w-3 mr-1" />
                {status?.status === 'not_configured' ? 'Not Configured'
                  : status?.status === 'error' ? `Disconnected · ${status?.error_code || 'error'}`
                  : 'Disconnected'}
              </Badge>
              {status?.message && (
                <p className="text-[10px] text-slate-500 text-right max-w-sm truncate" title={status.message} data-testid="investec-status-msg">
                  {status.message}
                </p>
              )}
              {status?.hint && (
                <p className="text-[10px] text-slate-400 text-right max-w-sm" data-testid="investec-status-hint">
                  {status.hint}
                </p>
              )}
            </div>
          )}
          <Button onClick={handleSync} disabled={syncing} className="gap-2">
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            Sync Balances
          </Button>
          <Button
            variant="outline"
            onClick={() => { setBulkSyncOpen(true); setBulkSelected(new Set()); setBulkResults(null); }}
            className="gap-2"
            data-testid="bulk-sync-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Bulk send to staging
          </Button>
          <Button
            variant="outline"
            onClick={() => { setBatchesDialogOpen(true); setBatches([]); setSelectedBatches(new Set()); handleLoadBatches(); }}
            className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="manage-batches-btn"
          >
            <Trash2 className="h-4 w-4" />
            Manage / purge batches
          </Button>
          <Button
            variant="outline"
            onClick={() => { setOrphansDialogOpen(true); setOrphans([]); handleLoadOrphans(); }}
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="fix-orphans-btn"
          >
            <AlertCircle className="h-4 w-4" />
            Fix orphaned allocations
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Wallet className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Balance</p>
                <p className="text-xl font-bold">{formatCurrency(status?.connected ? mappings?.mappings?.reduce((sum, m) => sum + (m.balance || 0), 0) : 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Accounts</p>
                <p className="text-xl font-bold">{accounts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <LinkIcon className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Mapped</p>
                <p className="text-xl font-bold">{mappings?.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Investor Accounts</p>
                <p className="text-xl font-bold">{mappings?.by_purpose?.investor?.length || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="accounts">All Accounts</TabsTrigger>
          <TabsTrigger value="investors">Investor Mappings</TabsTrigger>
          <TabsTrigger value="historical" data-testid="tab-historical-import">Historical Import</TabsTrigger>
          <TabsTrigger value="client-bank" data-testid="tab-client-bank">Client Repayments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Main Disbursement */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Banknote className="h-5 w-5 text-amber-600" />
                  Main Disbursement Account
                </CardTitle>
                <CardDescription>Primary account for client disbursements</CardDescription>
              </CardHeader>
              <CardContent>
                {mappings?.by_purpose?.main_disbursement?.length > 0 ? (
                  <div className="space-y-3">
                    {mappings.by_purpose.main_disbursement.map((m, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${m.is_primary ? 'border-amber-300 bg-amber-50' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{m.display_name || m.account_name}</p>
                            <p className="text-sm text-slate-500">{m.account_number}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{formatCurrency(m.balance)}</p>
                            {m.is_primary && <Badge variant="outline" className="text-amber-600">Primary</Badge>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-slate-500">
                    <Banknote className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                    <p>No main disbursement account configured</p>
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setActiveTab('accounts')}>
                      Configure Now
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <ApplyAccountMappingsCard token={token} />

            {/* Purpose Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Accounts by Purpose</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {PURPOSE_OPTIONS.map(opt => {
                    const purposeAccounts = mappings?.by_purpose?.[opt.value] || [];
                    const totalBalance = purposeAccounts.reduce((sum, a) => sum + (a.balance || 0), 0);
                    return (
                      <div key={opt.value} className="flex items-center justify-between p-2 rounded hover:bg-slate-50">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full bg-${opt.color}-500`} />
                          <span className="text-sm font-medium">{opt.label}</span>
                          <Badge variant="secondary" className="ml-1">{purposeAccounts.length}</Badge>
                        </div>
                        <span className="font-medium">{formatCurrency(totalBalance)}</span>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between p-2 rounded border-t pt-3 mt-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-300" />
                      <span className="text-sm font-medium text-slate-500">Unmapped</span>
                      <Badge variant="outline">{accounts.length - (mappings?.total || 0)}</Badge>
                    </div>
                    <span className="text-slate-500">-</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* All Accounts Tab */}
        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Investec Accounts</CardTitle>
                  <CardDescription>{accounts.length} accounts linked</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input 
                      placeholder="Search accounts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                  <Select value={filterPurpose} onValueChange={setFilterPurpose}>
                    <SelectTrigger className="w-40">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Accounts</SelectItem>
                      <SelectItem value="unmapped">Unmapped</SelectItem>
                      {PURPOSE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((acc) => {
                    const mapping = getMappingForAccount(acc.account_id);
                    return (
                      <TableRow key={acc.account_id}>
                        <TableCell>
                          <div>
                            {/* For an investor-linked account, the investor's actual
                                registered name is what matters here - not whichever
                                contact/signatory name the bank happens to have on file
                                for that account. Show it as the primary label, with
                                the bank's own account label as secondary context. */}
                            <p className="font-medium">{mapping?.investor_name || mapping?.display_name || acc.account_name}</p>
                            {mapping?.investor_name && (mapping?.display_name || acc.account_name) && (
                              <p className="text-xs text-slate-500">{mapping.display_name || acc.account_name}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{acc.account_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{acc.account_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {mapping ? (
                            <Badge className={`bg-${getPurposeColor(mapping.purpose)}-100 text-${getPurposeColor(mapping.purpose)}-700`}>
                              {PURPOSE_OPTIONS.find(p => p.value === mapping.purpose)?.label || mapping.purpose}
                              {mapping.is_primary && ' ★'}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-400">Unmapped</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(mapping?.balance || 0)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openMappingDialog(acc)} title="Configure mapping">
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-600 hover:text-green-700"
                              title="Sync transactions to staging"
                              onClick={() => { setSyncDialog({ open: true, account: acc }); setSyncResult(null); }}
                              data-testid={`sync-btn-${acc.account_id}`}
                            >
                              <ArrowDownToLine className="h-4 w-4" />
                            </Button>
                            {mapping && (
                              <Button size="sm" variant="ghost" onClick={() => removeMapping(acc.account_id)}>
                                <Unlink className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investor Mappings Tab */}
        <TabsContent value="investors">
          <Card>
            <CardHeader>
              <CardTitle>Investor Account Mappings</CardTitle>
              <CardDescription>Link Investec accounts to specific investors</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Investor</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Linked Account</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {investors.map((inv) => {
                    const mapping = mappings?.by_purpose?.investor?.find(m => m.investor_id === inv.id);
                    return (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">{inv.full_name}</TableCell>
                        <TableCell className="text-slate-500">{inv.email}</TableCell>
                        <TableCell>
                          {mapping ? (
                            <div>
                              <p className="font-medium">{mapping.display_name || mapping.account_name}</p>
                              <p className="text-xs text-slate-500">{mapping.account_number}</p>
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {mapping ? formatCurrency(mapping.balance) : '-'}
                        </TableCell>
                        <TableCell>
                          {mapping ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Linked
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600">
                              Not Linked
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Historical Bank CSV Import Tab */}
        <TabsContent value="historical" className="space-y-4">
          <HistoricalImportTab API_URL={API_URL} headers={authHeaders} mappings={mappings?.mappings || []} />
        </TabsContent>

        {/* Client-side (loan_book) bank reconciliation — separate from the
            investor-side tools above, matches real client repayments/
            drawdowns against the fund's own Main Account. */}
        <TabsContent value="client-bank" className="space-y-4">
          <ClientBankReconciliationTab API_URL={API_URL} headers={authHeaders} />
        </TabsContent>
      </Tabs>

      {/* Mapping Dialog */}
      <Dialog open={mappingDialog.open} onOpenChange={(open) => setMappingDialog({ ...mappingDialog, open })}>
        <DialogContent className="max-w-lg w-[95vw]">
          <DialogHeader>
            <DialogTitle>Configure Account Mapping</DialogTitle>
            <DialogDescription className="break-words">
              {mappingDialog.account?.account_name} ({mappingDialog.account?.account_number})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Purpose</Label>
              <Select value={mappingForm.purpose} onValueChange={(v) => setMappingForm({...mappingForm, purpose: v, investor_id: v !== 'investor' ? '' : mappingForm.investor_id})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div>
                        <span className="font-medium">{opt.label}</span>
                        <span className="text-xs text-slate-500 ml-2">{opt.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {mappingForm.purpose === 'investor' && (
              <div className="space-y-2">
                <Label>Linked Investor</Label>
                <Select value={mappingForm.investor_id} onValueChange={(v) => setMappingForm({...mappingForm, investor_id: v})}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select investor" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {investors
                      .filter(inv => {
                        // Show unmapped investors + the investor already mapped to THIS account
                        const currentAccountMapping = mappings?.mappings?.find(m => m.investec_account_id === mappingDialog.account?.account_id);
                        const currentInvestorId = currentAccountMapping?.investor_id;
                        if (inv.id === currentInvestorId) return true;
                        // Hide investors already mapped to OTHER accounts
                        const isMapped = mappings?.by_purpose?.investor?.some(m => m.investor_id === inv.id);
                        return !isMapped;
                      })
                      .map(inv => (
                        <SelectItem key={inv.id} value={inv.id}>
                          <span className="truncate block max-w-[350px]">{inv.full_name} ({inv.email})</span>
                        </SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {mappings?.by_purpose?.investor?.some(m => m.investec_account_id === mappingDialog.account?.account_id) && (
                  <p className="text-xs text-amber-600">This account is already mapped. Selecting a different investor will reassign it.</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Display Name (optional)</Label>
              <Input 
                value={mappingForm.display_name}
                onChange={(e) => setMappingForm({...mappingForm, display_name: e.target.value})}
                placeholder="Custom display name"
              />
            </div>

            {mappingForm.purpose === 'main_disbursement' && (
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="is_primary"
                  checked={mappingForm.is_primary}
                  onCheckedChange={(checked) => setMappingForm({...mappingForm, is_primary: checked})}
                />
                <Label htmlFor="is_primary" className="text-sm cursor-pointer">
                  Set as primary disbursement account
                </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingDialog({ open: false, account: null })}>
              Cancel
            </Button>
            <Button onClick={saveMapping} disabled={!mappingForm.purpose}>
              Save Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync to Staging Dialog */}
      {/* Bulk-Send-to-Staging Dialog */}
      <Dialog open={bulkSyncOpen} onOpenChange={(o) => { setBulkSyncOpen(o); if (!o) setBulkResults(null); }}>
        <DialogContent className="max-w-3xl" data-testid="bulk-sync-dialog">
          <DialogHeader>
            <DialogTitle>Bulk send to staging</DialogTitle>
            <DialogDescription>
              Select the Investec accounts you want to sync into staging in one go.
              All rules (dedup, auto-classification, RP-matching) run per account the same way as the single-account button.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From date</Label>
                <Input type="date" value={bulkForm.from_date} onChange={(e) => setBulkForm(f => ({ ...f, from_date: e.target.value }))} data-testid="bulk-from-date" />
              </div>
              <div>
                <Label>To date (blank = today)</Label>
                <Input type="date" value={bulkForm.to_date} onChange={(e) => setBulkForm(f => ({ ...f, to_date: e.target.value }))} data-testid="bulk-to-date" />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Accounts ({bulkSelected.size} selected of {mappings?.mappings?.length || 0})</Label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setBulkSelected(new Set((mappings?.mappings || []).map(m => m.investec_account_id || m.account_id)))} data-testid="bulk-select-all">Select all</Button>
                  <Button variant="outline" size="sm" onClick={() => setBulkSelected(new Set())} data-testid="bulk-clear-all">Clear</Button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded p-2 space-y-1">
                {(mappings?.mappings || []).map((m) => {
                  const id = m.investec_account_id || m.account_id;
                  const checked = bulkSelected.has(id);
                  return (
                    <label key={id} className="flex items-center gap-2 text-sm py-1 px-2 hover:bg-slate-50 rounded cursor-pointer" data-testid={`bulk-account-${id.slice(0, 8)}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(bulkSelected);
                          if (checked) next.delete(id); else next.add(id);
                          setBulkSelected(next);
                        }}
                      />
                      <span className="font-mono text-xs">{id.slice(0, 12)}…</span>
                      <span className="text-slate-500 text-xs">
                        {m.risk_participant_code ? `[${m.risk_participant_code}] ` : ''}
                        {m.investor_name || m.display_name || 'unnamed'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            {bulkResults && (
              <div className="border rounded p-3 bg-slate-50 space-y-2 text-sm" data-testid="bulk-results">
                <div className="font-medium">
                  Result: {bulkResults.total_created} txns pushed across {bulkResults.succeeded}/{bulkResults.requested} accounts
                </div>
                <div className="max-h-40 overflow-y-auto space-y-1 text-xs font-mono">
                  {bulkResults.results.map((r) => (
                    <div key={r.account_id} className={r.ok ? 'text-emerald-700' : 'text-red-600'}>
                      {r.ok ? '✓' : '✗'} {r.account_id.slice(0, 12)}… — {r.ok ? `${r.created} created` : r.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkSyncOpen(false)} disabled={bulkRunning}>Close</Button>
            <Button
              onClick={handleBulkSyncToStaging}
              disabled={bulkRunning || bulkSelected.size === 0 || !bulkForm.from_date}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bulk-sync-run"
            >
              {bulkRunning ? 'Syncing…' : `Send ${bulkSelected.size} account(s) to staging`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={syncDialog.open} onOpenChange={o => { setSyncDialog(p => ({...p, open: o})); if (!o) { setPreviewRows([]); setSelectedPreviewIds(new Set()); setOverrideSelection(new Set()); setSyncResult(null); } }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowDownToLine className="h-4 w-4" />Sync Transactions to Staging</DialogTitle>
            <DialogDescription>
              Fetch transactions from account <strong>{syncDialog.account?.account_name || syncDialog.account?.account_id}</strong>. Use <strong>Preview</strong> to select specific txns, or <strong>Sync All</strong> to push everything in the range.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs">From Date</Label>
                <Input type="date" value={syncForm.from_date} onChange={e => setSyncForm(p => ({...p, from_date: e.target.value}))} data-testid="sync-from-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To Date</Label>
                <Input type="date" value={syncForm.to_date} onChange={e => setSyncForm(p => ({...p, to_date: e.target.value}))} data-testid="sync-to-date" />
              </div>
            </div>

            <div className="flex gap-2 items-center">
              <Button variant="outline" onClick={handleLoadPreview} disabled={loadingPreview || !syncForm.from_date} className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="preview-btn">
                {loadingPreview ? 'Loading...' : 'Preview & Select Transactions'}
              </Button>
              {previewRows.length > 0 && (
                <span className="text-sm text-slate-600">
                  {selectedPreviewIds.size} of {previewRows.filter(r => r.dedup_status === 'new').length} new selected
                </span>
              )}
            </div>

            {previewRows.length > 0 && (
              <div className="border rounded-md max-h-96 overflow-y-auto" data-testid="preview-rows-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-1 text-left w-8">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const newRows = previewRows.filter(r => r.dedup_status === 'new' && (r.line_key || r.investec_uuid));
                            return newRows.length > 0 && newRows.every(r => selectedPreviewIds.has(r.line_key || r.investec_uuid));
                          })()}
                          onChange={(e) => {
                            const newRows = previewRows.filter(r => r.dedup_status === 'new' && (r.line_key || r.investec_uuid));
                            if (e.target.checked) setSelectedPreviewIds(new Set(newRows.map(r => r.line_key || r.investec_uuid)));
                            else setSelectedPreviewIds(new Set());
                          }}
                          data-testid="preview-select-all"
                        />
                      </th>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                      <th className="px-2 py-1 text-left">Description</th>
                      <th className="px-2 py-1 text-left w-20">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => {
                      const isDup = r.dedup_status === 'duplicate';
                      // One key per bank line (Investec id, else its bank reference) —
                      // two same-day, same-amount payments are separate rows.
                      const rowKey = r.line_key || r.investec_uuid;
                      const checked = !!(rowKey && selectedPreviewIds.has(rowKey));
                      return (
                        <tr key={rowKey || i} className={`border-t ${isDup ? 'bg-slate-100 text-slate-400' : 'hover:bg-slate-50'}`}>
                          <td className="px-2 py-1">
                            <input
                              type="checkbox"
                              disabled={isDup || !rowKey}
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(selectedPreviewIds);
                                if (e.target.checked) next.add(rowKey);
                                else next.delete(rowKey);
                                setSelectedPreviewIds(next);
                              }}
                              data-testid={`preview-row-${i}`}
                            />
                          </td>
                          <td className="px-2 py-1">{r.transaction_date}</td>
                          <td className={`px-2 py-1 text-right font-mono ${r.amount < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                            {Number(r.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, signDisplay: 'always' })}
                          </td>
                          <td className="px-2 py-1">{r.description}</td>
                          <td className="px-2 py-1">
                            {isDup ? <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-500 text-[10px]">DUPLICATE</span> : <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px]">NEW</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {syncResult && (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm space-y-1">
                <p className="font-medium text-green-800">Sync Complete</p>
                <p className="text-green-700">Total fetched: <strong>{syncResult.total_fetched}</strong></p>
                <p className="text-green-700">Staged: <strong>{syncResult.staged_count}</strong> transactions</p>
                <p className="text-green-700">PTC pattern matched: <strong>{syncResult.matched_investors}</strong></p>
                {syncResult.unmatched_investors > 0 && <p className="text-amber-700">PTC pattern but investor unmatched: <strong>{syncResult.unmatched_investors}</strong></p>}
                {syncResult.no_pattern > 0 && <p className="text-slate-600">No PTC pattern (needs manual review): <strong>{syncResult.no_pattern}</strong></p>}
                {syncResult.skipped_duplicates > 0 && <p className="text-gray-500">Skipped (duplicates): <strong>{syncResult.skipped_duplicates}</strong></p>}
                {syncResult.override_applied > 0 && <p className="text-blue-700">Forced through (override): <strong>{syncResult.override_applied}</strong></p>}

                {/* Blocked-duplicates panel with override. Admin ticks the
                    rows that were WRONGLY flagged as dupes and clicks
                    "Force selected through" — the same sync call is
                    replayed with override_uuids set, bypassing dedup only
                    for those specific uuids. */}
                {(syncResult.skipped_duplicate_details || []).length > 0 && (
                  <div className="mt-3 pt-2 border-t border-amber-200">
                    <p className="text-amber-800 font-medium text-sm mb-1">
                      Duplicates that were BLOCKED — review and override if any are legitimate
                    </p>
                    <div className="max-h-56 overflow-y-auto border rounded bg-white">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="p-1 text-left w-8">
                              <input
                                type="checkbox"
                                onChange={(e) => {
                                  const all = new Set(
                                    e.target.checked
                                      ? (syncResult.skipped_duplicate_details || [])
                                          .map(d => d.override_key || d.investec_uuid)
                                          .filter(Boolean)
                                      : []
                                  );
                                  setOverrideSelection(all);
                                }}
                                data-testid="override-select-all"
                              />
                            </th>
                            <th className="p-1 text-left">Date</th>
                            <th className="p-1 text-right">Amount</th>
                            <th className="p-1 text-left">Description</th>
                            <th className="p-1 text-left">Match reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(syncResult.skipped_duplicate_details || []).map((d) => {
                            // The backend always provides `override_key`, one per bank
                            // line: the Investec id, else `ref:{account}|{UBP…}` (the
                            // bank's own reference), else `sig:{account}|{date}|{amount}`.
                            const key = d.override_key || d.investec_uuid || `${d.transaction_date}|${d.amount}|${d.description}`;
                            return (
                              <tr key={key} className={overrideSelection.has(key) ? 'bg-blue-50' : ''}>
                                <td className="p-1">
                                  <input
                                    type="checkbox"
                                    checked={overrideSelection.has(key)}
                                    onChange={() => {
                                      setOverrideSelection(prev => {
                                        const next = new Set(prev);
                                        if (next.has(key)) next.delete(key);
                                        else next.add(key);
                                        return next;
                                      });
                                    }}
                                    data-testid={`override-cb-${key}`}
                                  />
                                </td>
                                <td className="p-1 font-mono">{d.transaction_date}</td>
                                <td className="p-1 text-right font-mono">R {Number(d.amount || 0).toFixed(2)}</td>
                                <td className="p-1 truncate max-w-md">{d.description}</td>
                                <td className="p-1 text-gray-500">{d.match_reason}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <Button
                      size="sm"
                      className="mt-2 bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={overrideSelection.size === 0 || syncing_import}
                      onClick={() => handleSyncToStaging(Array.from(overrideSelection), Array.from(overrideSelection))}
                      data-testid="force-override-btn"
                    >
                      Force {overrideSelection.size} selected duplicate(s) through
                    </Button>
                    <p className="text-xs text-gray-500 mt-1 italic">
                      Ticks the rows that dedup wrongly blocked. Clicking will re-run the sync with those UUIDs forced past the dedup check.
                    </p>
                  </div>
                )}
                {syncResult.dedup_diagnostics && (
                  <div className="mt-2 pt-2 border-t border-green-200 text-[11px] text-slate-600 font-mono space-y-0.5" data-testid="sync-dedup-diagnostics">
                    <p className="text-slate-700 font-sans font-medium text-xs">Dedup check ran against:</p>
                    <p>• {syncResult.dedup_diagnostics.scanned_committed_bank_rows} committed bank rows</p>
                    <p>• {syncResult.dedup_diagnostics.scanned_staged_bank_rows} pending staging rows</p>
                    <p>• {syncResult.dedup_diagnostics.known_uuids} distinct Investec UUIDs known</p>
                    <p>• {syncResult.dedup_diagnostics.known_account_sigs} (account+date+abs amount) sigs</p>
                    {syncResult.dedup_diagnostics.scanned_committed_bank_rows === 0 && (
                      <p className="text-red-600 font-sans">⚠ 0 committed bank rows scanned — dedup has nothing to compare against. Your existing rows may have a non-bank `source` value.</p>
                    )}
                  </div>
                )}
                {syncResult.batch_id && <p className="text-xs text-gray-500 mt-1">Review in Transactions → Import & Approve tab using batch ID: <code className="font-mono">{syncResult.batch_id?.slice(0,8)}...</code></p>}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSyncDialog({ open: false, account: null })}>Close</Button>
            {previewRows.length > 0 ? (
              <Button onClick={() => handleSyncToStaging(Array.from(selectedPreviewIds))} disabled={syncing_import || selectedPreviewIds.size === 0} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-selected-btn">
                {syncing_import ? 'Sending...' : `Send ${selectedPreviewIds.size} Selected to Staging`}
              </Button>
            ) : (
              <Button onClick={() => handleSyncToStaging(null)} disabled={syncing_import || !syncForm.from_date} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-sync-btn">
                {syncing_import ? 'Syncing...' : 'Sync All to Staging'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage / purge batches dialog */}
      <Dialog open={batchesDialogOpen} onOpenChange={o => { setBatchesDialogOpen(o); if (!o) { setBatches([]); setSelectedBatches(new Set()); setAlsoDeleteCommitted(false); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-4 w-4" />
              Manage / purge staging batches
            </DialogTitle>
            <DialogDescription>
              Every bulk-send-to-staging run creates one batch per Investec account. Select the runaway batch(es) and purge them.
              Enable &quot;also delete committed&quot; ONLY if the batch was already promoted from staging to reconciliation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-end gap-3">
              <div>
                <Label className="text-xs">Look back (days)</Label>
                <Input type="number" min={1} max={365} value={batchesDays}
                  onChange={e => setBatchesDays(parseInt(e.target.value) || 7)}
                  className="w-24" data-testid="batches-days" />
              </div>
              <Button variant="outline" onClick={handleLoadBatches} disabled={batchesLoading} data-testid="batches-refresh">
                {batchesLoading ? 'Loading…' : 'Refresh'}
              </Button>
              <div className="flex items-center gap-2 ml-auto">
                <Checkbox
                  id="also-delete-committed"
                  checked={alsoDeleteCommitted}
                  onCheckedChange={setAlsoDeleteCommitted}
                  data-testid="also-delete-committed"
                />
                <Label htmlFor="also-delete-committed" className="text-sm text-red-700 cursor-pointer">
                  Also delete committed rows in Reconciliation
                </Label>
              </div>
            </div>

            <div className="border rounded overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Batch</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                    <TableHead className="text-right">Approved</TableHead>
                    <TableHead className="text-right">No-investor</TableHead>
                    <TableHead className="text-right">Distinct investors</TableHead>
                    <TableHead>Date range</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-6">No batches loaded — click Refresh.</TableCell></TableRow>
                  )}
                  {batches.map(b => (
                    <TableRow key={b.batch_id} data-testid={`batch-row-${b.batch_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedBatches.has(b.batch_id)}
                          onCheckedChange={(v) => {
                            setSelectedBatches(prev => {
                              const n = new Set(prev);
                              if (v) n.add(b.batch_id); else n.delete(b.batch_id);
                              return n;
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div>{b.batch_name || '—'}</div>
                        <div className="text-slate-400">{(b.batch_id || '').slice(0, 12)}…</div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{(b.account_id || '—').slice(0, 12)}…</TableCell>
                      <TableCell className="text-xs">{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-right font-medium">{b.row_count}</TableCell>
                      <TableCell className="text-right">{b.approved_count}</TableCell>
                      <TableCell className={`text-right ${b.untagged_count > 0 ? 'text-red-600 font-medium' : ''}`}>{b.untagged_count}</TableCell>
                      <TableCell className="text-right">{b.distinct_investor_count}</TableCell>
                      <TableCell className="text-xs">{b.min_date} → {b.max_date}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {batches.length > 0 && (
              <div className="text-xs text-slate-500">
                Selected: {selectedBatches.size} batch(es) · Total rows: {
                  batches.filter(b => selectedBatches.has(b.batch_id)).reduce((s, b) => s + (b.row_count || 0), 0)
                }
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchesDialogOpen(false)} disabled={purging}>Close</Button>
            <Button
              variant="outline"
              onClick={handlePurgeAll}
              disabled={purging}
              className="border-red-600 text-red-700 hover:bg-red-50 mr-auto"
              data-testid="purge-all-btn"
            >
              {purging ? 'Working…' : 'Purge ALL Investec staging'}
            </Button>
            <Button
              onClick={handlePurgeBatches}
              disabled={purging || selectedBatches.size === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="purge-batches-btn"
            >
              {purging ? 'Purging…' : `Purge ${selectedBatches.size} batch(es)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphaned Allocations dialog — clean allocations pointing at deleted bank rows */}
      <Dialog open={orphansDialogOpen} onOpenChange={(o) => { setOrphansDialogOpen(o); if (!o) setOrphans([]); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="orphans-dialog">
          <DialogHeader>
            <DialogTitle>Orphaned allocations</DialogTitle>
            <DialogDescription>
              Allocations whose bank transaction no longer exists (usually caused by a prior purge).
              Deleting them will reset any recorded txn (with zero remaining allocations) back to <code>pending</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {orphansLoading ? (
              <div className="py-8 text-center text-slate-500">Scanning&hellip;</div>
            ) : orphans.length === 0 ? (
              <div className="py-8 text-center text-slate-500">No orphaned allocations found. Every allocation still points at a live bank row.</div>
            ) : (
              <div className="border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recorded txn id</TableHead>
                      <TableHead>Missing bank id</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orphans.slice(0, 200).map((o, i) => (
                      <TableRow key={o.id || i} data-testid={`orphan-row-${i}`}>
                        <TableCell className="text-xs font-mono">{(o.recorded_txn_id || '—').toString().slice(0, 12)}…</TableCell>
                        <TableCell className="text-xs font-mono">{(o.bank_transaction_id || '—').toString().slice(0, 12)}…</TableCell>
                        <TableCell className="text-xs text-right font-mono">{formatCurrency(o.amount)}</TableCell>
                        <TableCell className="text-xs">{o.created_at?.slice(0, 10) || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {orphans.length > 200 && (
                  <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-t">
                    Showing first 200 of {orphans.length}. Delete will clean up all.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrphansDialogOpen(false)} disabled={orphansDeleting}>Close</Button>
            <Button
              variant="outline"
              onClick={handleLoadOrphans}
              disabled={orphansLoading || orphansDeleting}
              data-testid="orphans-refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${orphansLoading ? 'animate-spin' : ''}`} />
              Rescan
            </Button>
            <Button
              onClick={handleDeleteOrphans}
              disabled={orphansDeleting || orphans.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="orphans-delete-btn"
            >
              {orphansDeleting ? 'Deleting…' : `Delete ${orphans.length} orphan(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminInvestecPage;
